import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { Icon } from '@iconify/react'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import { formatRouletteUserError } from '@/lib/formatRouletteUserError'
import {
  getRoulettePrizes,
  setRoulettePrizes,
  probabilitiesSumValid,
  type RoulettePrizeType,
  type CouponPrizePayload,
  type LuckyNumberPrizePayload,
  type PixPrizePayload,
  type NonePrizePayload,
} from '@/lib/roulette'
import { remainderRouletteProbabilityPercentForSlots } from '@/lib/rouletteProbability'
import {
  getRouletteProbabilityBudget,
  getRouletteSlotProbabilityHint,
} from '@/lib/rouletteProbabilityBudget'
import {
  emptyPayload,
  reconcileSlots,
  buildSlotInput,
  rouletteProbabilityToPercent,
  type RoletaSlotState,
} from '@/pages/CampanhasPage/roletaSlotTypes'
import { RoletaPrizeTypeFields } from '@/pages/CampanhasPage/RoletaPrizeTypeFields'
import { RoletaProbabilityInput } from '@/pages/CampanhasPage/RoletaProbabilityInput'
import { RoletaProbabilitySummary } from '@/pages/CampanhasPage/RoletaProbabilitySummary'
import { RoletaSlotProbabilityHint } from '@/pages/CampanhasPage/RoletaSlotProbabilityHint'
import styles from './CampanhasPage.module.css'
import type { RoletaSaveTask } from './roletaSaveTask'

const PRIZE_TYPES: { value: RoulettePrizeType; label: string }[] = [
  { value: 'coupon', label: 'Cupom de desconto' },
  { value: 'lucky_number', label: 'Número da sorte' },
  { value: 'pix', label: 'PIX' },
  { value: 'none', label: 'Sem prêmio' },
]

const PRIZE_TYPE_HELP: Record<RoulettePrizeType, string> = {
  coupon:
    'O comprador ganha um cupom com as regras abaixo. O código é criado automaticamente no sorteio.',
  lucky_number:
    'O comprador recebe números da sorte extras, conforme a quantidade configurada.',
  pix: 'O comprador ganha um valor em PIX quando esta fatia for sorteada.',
  none: 'Fatia neutra para equilibrar probabilidades. O comprador não recebe prêmio neste espaço.',
}

function prizesToSlots(
  prizes: Awaited<ReturnType<typeof getRoulettePrizes>>,
  positions: number
): RoletaSlotState[] {
  const mapped: RoletaSlotState[] = prizes
    .filter((p) => p.position >= 1 && p.position <= positions)
    .sort((a, b) => a.position - b.position)
    .map((p) => ({
      position: p.position,
      prize_type: p.prize_type,
      probability: rouletteProbabilityToPercent(p.probability),
      prize_payload:
        (p.prize_payload as RoletaSlotState['prize_payload']) ??
        emptyPayload(p.prize_type),
    }))
  return reconcileSlots(mapped, positions)
}

export interface RoletaPrizesEditorProps {
  campaignId: number
  positions: number
  /** Posições já salvas no servidor; usadas no carregamento para não descartar slots da API. */
  savedPositions?: number
  token: string | null
  configExists: boolean
  onSaveAll: () => Promise<boolean>
  onSaveStateChange: (state: RoletaSaveTask | null) => void
  savingAll?: boolean
  saveDisabled?: boolean
  onDraftChange?: (slots: RoletaSlotState[]) => void
}

export function RoletaPrizesEditor({
  campaignId,
  positions,
  savedPositions,
  token,
  configExists,
  onSaveAll,
  onSaveStateChange,
  savingAll = false,
  saveDisabled = false,
  onDraftChange,
}: RoletaPrizesEditorProps) {
  const { runWithStepUp } = useCampaignTotpStepUp()
  const [slots, setSlots] = useState<RoletaSlotState[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isPrizesExpanded, setIsPrizesExpanded] = useState(true)
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const positionsRef = useRef(positions)
  positionsRef.current = positions
  const savedPositionsRef = useRef(savedPositions)
  savedPositionsRef.current = savedPositions
  const rawPrizesRef = useRef<Awaited<ReturnType<typeof getRoulettePrizes>>>([])

  useEffect(() => {
    let cancelled = false
    const loadPrizes = async () => {
      if (!configExists) {
        setIsLoading(false)
        setSlots([])
        rawPrizesRef.current = []
        return
      }
      const uiPos = positionsRef.current
      const serverPos = savedPositionsRef.current ?? 0
      const pos = Math.max(uiPos, serverPos)
      if (pos < 2) {
        setIsLoading(false)
        return
      }
      setIsLoading(true)
      setLoadFailed(false)
      setSavedSnapshot(null)
      setErrorMessage(null)
      try {
        const prizes = await getRoulettePrizes(campaignId, token ?? undefined)
        if (cancelled) return
        rawPrizesRef.current = prizes
        const next =
          prizes.length > 0 ? prizesToSlots(prizes, pos) : reconcileSlots([], pos)
        setSlots(next)
        setSavedSnapshot(JSON.stringify(next))
      } catch {
        if (cancelled) return
        setLoadFailed(true)
        setErrorMessage('Não foi possível carregar os prêmios.')
        rawPrizesRef.current = []
        setSlots(reconcileSlots([], pos))
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    void loadPrizes()
    return () => {
      cancelled = true
    }
  }, [campaignId, token, configExists])

  useEffect(() => {
    if (!configExists || positions < 2 || isLoading) return
    setSlots((prev) => {
      if (rawPrizesRef.current.length > 0) {
        const fromApi = prizesToSlots(rawPrizesRef.current, positions)
        if (prev.length === 0) return fromApi
        const edited = new Map(prev.map((s) => [s.position, s]))
        return fromApi.map((slot) => edited.get(slot.position) ?? slot)
      }
      return reconcileSlots(prev, positions)
    })
  }, [positions, configExists, isLoading])

  useEffect(() => {
    if (!configExists || positions < 2 || isLoading) return
    onDraftChange?.(slots)
  }, [slots, configExists, positions, isLoading, onDraftChange])

  const updateSlot = useCallback((index: number, patch: Partial<RoletaSlotState>) => {
    setSlots((prev) => {
      const next = [...prev]
      const current = next[index]
      if (!current) return prev
      if (patch.prize_type !== undefined && patch.prize_type !== current.prize_type) {
        next[index] = {
          ...current,
          ...patch,
          prize_payload: emptyPayload(patch.prize_type),
        }
      } else {
        next[index] = { ...current, ...patch }
      }
      return next
    })
  }, [])

  const fillRemainderProbability = useCallback((index: number) => {
    setSlots((prev) => {
      const next = [...prev]
      const current = next[index]
      if (!current) return prev
      const remainder = remainderRouletteProbabilityPercentForSlots(prev, index)
      next[index] = { ...current, probability: remainder }
      return next
    })
  }, [])

  const updatePayloadCoupon = useCallback(
    (index: number, patch: Partial<CouponPrizePayload>) => {
      setSlots((prev) => {
        const next = [...prev]
        const current = next[index]
        if (!current || current.prize_type !== 'coupon' || !current.prize_payload)
          return prev
        next[index] = {
          ...current,
          prize_payload: { ...(current.prize_payload as CouponPrizePayload), ...patch },
        }
        return next
      })
    },
    []
  )

  const updatePayloadLucky = useCallback(
    (index: number, patch: Partial<LuckyNumberPrizePayload>) => {
      setSlots((prev) => {
        const next = [...prev]
        const current = next[index]
        if (!current || current.prize_type !== 'lucky_number' || !current.prize_payload)
          return prev
        next[index] = {
          ...current,
          prize_payload: {
            ...(current.prize_payload as LuckyNumberPrizePayload),
            ...patch,
          },
        }
        return next
      })
    },
    []
  )

  const updatePayloadPix = useCallback(
    (index: number, patch: Partial<PixPrizePayload>) => {
      setSlots((prev) => {
        const next = [...prev]
        const current = next[index]
        if (!current || current.prize_type !== 'pix' || !current.prize_payload)
          return prev
        next[index] = {
          ...current,
          prize_payload: { ...(current.prize_payload as PixPrizePayload), ...patch },
        }
        return next
      })
    },
    []
  )

  const updatePayloadNone = useCallback(
    (index: number, patch: Partial<NonePrizePayload>) => {
      setSlots((prev) => {
        const next = [...prev]
        const current = next[index]
        if (!current || current.prize_type !== 'none' || !current.prize_payload)
          return prev
        next[index] = {
          ...current,
          prize_payload: { ...(current.prize_payload as NonePrizePayload), ...patch },
        }
        return next
      })
    },
    []
  )

  const validate = useCallback(() => {
    const input = slots.map(buildSlotInput)
    if (!probabilitiesSumValid(input)) {
      setErrorMessage('A soma das probabilidades deve ser 100%.')
      setIsPrizesExpanded(true)
      return false
    }
    return true
  }, [slots])

  const handleSave = useCallback(async () => {
    if (!configExists || !token || isLoading || loadFailed || !validate()) return false
    const input = slots.map(buildSlotInput)
    setErrorMessage(null)
    setIsSaving(true)
    try {
      const saved = await runWithStepUp(() =>
        setRoulettePrizes(campaignId, { prizes: input }, token)
      )
      rawPrizesRef.current = saved
      setSavedSnapshot(JSON.stringify(slots))
      return true
    } catch (err) {
      setErrorMessage(formatRouletteUserError(err))
      return false
    } finally {
      setIsSaving(false)
    }
  }, [
    campaignId,
    token,
    configExists,
    isLoading,
    loadFailed,
    validate,
    slots,
    runWithStepUp,
  ])

  const hasChanges = savedSnapshot !== null && JSON.stringify(slots) !== savedSnapshot
  useEffect(() => {
    onSaveStateChange(
      isLoading || loadFailed || !configExists
        ? null
        : {
            hasChanges,
            saving: isSaving,
            save: handleSave,
            validate,
          }
    )
  }, [
    isLoading,
    loadFailed,
    configExists,
    hasChanges,
    isSaving,
    handleSave,
    validate,
    onSaveStateChange,
  ])
  useEffect(() => () => onSaveStateChange(null), [onSaveStateChange])

  const budget = useMemo(() => getRouletteProbabilityBudget(slots), [slots])

  if (!configExists) {
    return (
      <section className={styles.panelCard}>
        <div className={styles.panelHeader}>
          <h3 className={styles.sectionTitle}>Prêmios da roleta</h3>
          <p className={styles.sectionSubtitle}>
            Crie primeiro a configuração da roleta acima para definir os prêmios.
          </p>
        </div>
        <div className={styles.emptyPanel}>
          <div className={styles.emptyPanelIcon}>
            <Icon icon="mdi:circle-slice-8" />
          </div>
          <h4>Configuração necessária</h4>
          <p>Salve a configuração da roleta antes de definir os prêmios.</p>
        </div>
      </section>
    )
  }

  if (isLoading) {
    return (
      <section className={styles.panelCard}>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner} />
          <span>Carregando prêmios...</span>
        </div>
      </section>
    )
  }

  return (
    <section
      className={`${styles.panelCard} ${!isPrizesExpanded ? styles.panelCardCollapsed : ''}`}
      aria-labelledby="roleta-prizes-title"
    >
      <div className={styles.panelHeaderWithCollapse}>
        <div className={styles.panelHeaderMain}>
          <h3 id="roleta-prizes-title" className={styles.sectionTitle}>
            Prêmios da roleta
          </h3>
          <p className={styles.sectionSubtitle}>
            {isPrizesExpanded
              ? 'Defina o tipo e a probabilidade de cada posição (vírgula para decimais). A soma deve ser exatamente 100%.'
              : `${slots.length} posição(ões) configurada(s). Total: ${budget.totalDisplay}%. Expanda para editar os prêmios.`}
          </p>
        </div>
        <div className={styles.panelHeaderActions}>
          <span
            className={
              budget.status === 'exact'
                ? styles.successBanner
                : budget.status === 'over'
                  ? styles.errorBanner
                  : styles.warningBanner
            }
            style={{ padding: '8px 12px', fontSize: 'var(--font-size-xs)' }}
          >
            Total: {budget.totalDisplay}%
            {budget.status !== 'exact' && (
              <>
                {' '}
                ·{' '}
                {budget.status === 'over'
                  ? `excedente ${budget.remainderDisplay}%`
                  : `faltam ${budget.remainderDisplay}%`}
              </>
            )}
          </span>
          <button
            type="button"
            className={styles.primaryButton}
            disabled={saveDisabled || isSaving}
            onClick={() => void onSaveAll()}
          >
            {savingAll || isSaving ? 'Salvando...' : 'Salvar roleta'}
          </button>
          <button
            type="button"
            className={styles.panelCollapseToggle}
            onClick={() => setIsPrizesExpanded((expanded) => !expanded)}
            aria-expanded={isPrizesExpanded}
            aria-controls="roleta-prizes-panel"
            aria-label={
              isPrizesExpanded
                ? 'Recolher configurações dos prêmios da roleta'
                : 'Abrir configurações dos prêmios da roleta'
            }
          >
            <Icon icon="mdi:gift-outline" aria-hidden />
            <span>Configurar prêmios</span>
            <Icon
              icon={isPrizesExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'}
              aria-hidden
            />
          </button>
        </div>
      </div>

      {errorMessage && (
        <div className={styles.errorBanner} role="alert">
          {errorMessage}
        </div>
      )}

      {isPrizesExpanded ? (
        <fieldset
          id="roleta-prizes-panel"
          className={styles.panelFields}
          disabled={savingAll || isSaving || loadFailed}
        >
          <RoletaProbabilitySummary budget={budget} positionsCount={slots.length} />

          <div className={styles.contentStack} style={{ gap: 'var(--spacing-lg)' }}>
            {slots.map((slot, index) => {
              const slotHint = getRouletteSlotProbabilityHint(slots, index)
              const canFillRemainder = slotHint.availableForPositionPercent > 0

              return (
                <div
                  key={`slot-${index}`}
                  className={styles.formSection}
                  style={{ borderLeft: '3px solid rgba(94, 23, 235, 0.3)' }}
                >
                  <h4>Posição {slot.position}</h4>
                  <div
                    className={styles.formGrid}
                    style={{
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    }}
                  >
                    <div className={styles.field}>
                      <label htmlFor={`roleta-type-${index}`}>Tipo de prêmio</label>
                      <select
                        id={`roleta-type-${index}`}
                        value={slot.prize_type}
                        onChange={(e) =>
                          updateSlot(index, {
                            prize_type: e.target.value as RoulettePrizeType,
                          })
                        }
                        className={styles.input}
                        aria-describedby={`roleta-type-help-${index}`}
                      >
                        {PRIZE_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                      <p
                        id={`roleta-type-help-${index}`}
                        style={{
                          margin: 'var(--spacing-xs) 0 0',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-muted)',
                        }}
                      >
                        {PRIZE_TYPE_HELP[slot.prize_type]}
                      </p>
                    </div>
                    <div className={styles.field}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 'var(--spacing-sm)',
                          flexWrap: 'wrap',
                          marginBottom: '4px',
                        }}
                      >
                        <label
                          htmlFor={`roleta-prob-${index}`}
                          style={{ marginBottom: 0 }}
                        >
                          Probabilidade (%)
                        </label>
                        <button
                          type="button"
                          className={styles.ghostButton}
                          style={{
                            fontSize: '11px',
                            padding: '2px 10px',
                            lineHeight: 1.3,
                            minHeight: 'auto',
                          }}
                          onClick={() => fillRemainderProbability(index)}
                          disabled={!canFillRemainder}
                          aria-label={`Preencher posição ${slot.position} com o restante (${slotHint.availableForPositionDisplay}%)`}
                        >
                          Usar restante
                        </button>
                      </div>
                      <RoletaProbabilityInput
                        id={`roleta-prob-${index}`}
                        value={slot.probability}
                        onChange={(probability) => updateSlot(index, { probability })}
                        describedBy={`roleta-prob-hint-${index}`}
                      />
                      <RoletaSlotProbabilityHint
                        id={`roleta-prob-hint-${index}`}
                        hint={slotHint}
                        canFillRemainder={canFillRemainder}
                      />
                    </div>
                  </div>

                  <RoletaPrizeTypeFields
                    prizeType={slot.prize_type}
                    couponPayload={
                      slot.prize_type === 'coupon'
                        ? (slot.prize_payload as CouponPrizePayload)
                        : null
                    }
                    luckyPayload={
                      slot.prize_type === 'lucky_number'
                        ? (slot.prize_payload as LuckyNumberPrizePayload)
                        : null
                    }
                    pixPayload={
                      slot.prize_type === 'pix'
                        ? (slot.prize_payload as PixPrizePayload)
                        : null
                    }
                    nonePayload={
                      slot.prize_type === 'none'
                        ? (slot.prize_payload as NonePrizePayload)
                        : null
                    }
                    onCouponChange={(patch) => updatePayloadCoupon(index, patch)}
                    onLuckyChange={(patch) => updatePayloadLucky(index, patch)}
                    onPixChange={(patch) => updatePayloadPix(index, patch)}
                    onNoneChange={(patch) => updatePayloadNone(index, patch)}
                  />
                </div>
              )
            })}
          </div>
        </fieldset>
      ) : null}
    </section>
  )
}
